import { createFileRoute } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api.js'
import { useMe } from '../lib/me.js'
import { PageHeader } from '../components/PageHeader.js'
import { DataTable, type Column } from '../components/DataTable.js'
import { StatusPill } from '../components/StatusPill.js'
import { Money } from '../components/Money.js'

export const Route = createFileRoute('/_app/settlements')({
  component: SettlementsPage,
})

interface SettlementRow {
  id: string
  academyId: string
  academyName: string
  periodStart: string
  periodEnd: string
  grossCollectedInr: number
  surchargeTotalInr: number
  gatewayCostAbsorbedInr: number
  netOwedInr: number
  status: 'pending' | 'paid_out'
  paidOutAt: string | null
  referenceNote: string | null
}

function fmtPeriod(start: string): string {
  const [y, m] = start.split('-').map(Number)
  const month = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][
    (m ?? 1) - 1
  ]
  return `${month} ${y}`
}

function SettlementsPage() {
  const qc = useQueryClient()
  const { me } = useMe()
  const isSuper = me?.role === 'super_admin'

  const settlements = useQuery({
    queryKey: ['settlements'],
    queryFn: async () => {
      const res = await api<SettlementRow[]>('/api/v1/settlements')
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
  })

  const markPaidOut = useMutation({
    mutationFn: async (id: string) => {
      const res = await api<SettlementRow>(`/api/v1/settlements/${id}/mark-paid-out`, {
        method: 'POST',
        body: JSON.stringify({}),
      })
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
    // Merge the server-returned fields into the existing row instead of
    // refetching. Merge (not replace) because mark-paid-out returns the base
    // settlement row WITHOUT the join-derived academyName — replacing would
    // blank that column.
    onSuccess: (updated) => {
      qc.setQueryData<SettlementRow[]>(['settlements'], (rows) =>
        (rows ?? []).map((s) => (s.id === updated.id ? { ...s, ...updated } : s)),
      )
    },
  })

  const renderAction = (s: SettlementRow) => {
    if (isSuper && s.status === 'pending') {
      return (
        <button
          onClick={() => {
            if (confirm(`Mark ${s.academyName} ${fmtPeriod(s.periodStart)} paid out?`))
              markPaidOut.mutate(s.id)
          }}
          className="text-xs text-fg-muted hover:text-success"
        >
          Mark paid out
        </button>
      )
    }
    if (s.paidOutAt) {
      return (
        <span className="text-xs text-fg-subtle">
          {new Date(s.paidOutAt).toLocaleDateString('en-IN')}
        </span>
      )
    }
    return null
  }

  const columns: Column<SettlementRow>[] = [
    {
      key: 'period',
      header: 'Period',
      render: (s) => (
        <div>
          <span className="font-medium text-fg">{fmtPeriod(s.periodStart)}</span>
          {isSuper ? (
            <div className="text-xs text-fg-subtle">{s.academyName}</div>
          ) : null}
        </div>
      ),
    },
    {
      key: 'gross',
      header: 'Collected',
      align: 'right',
      render: (s) => <Money paise={s.grossCollectedInr} variant="data" />,
    },
    {
      key: 'absorbed',
      header: 'Gateway cost',
      align: 'right',
      render: (s) => <Money paise={s.gatewayCostAbsorbedInr} variant="data" />,
    },
    {
      key: 'net',
      header: 'Net owed',
      align: 'right',
      render: (s) => (
        <Money paise={s.netOwedInr} variant="data" className="font-semibold text-fg" />
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (s) => (
        <StatusPill tone={s.status === 'paid_out' ? 'active' : 'pending'}>
          {s.status === 'paid_out' ? 'Paid out' : 'Pending'}
        </StatusPill>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: renderAction,
    },
  ]

  return (
    <div className="space-y-8">
      <PageHeader
        title="Settlements"
        description={
          isSuper
            ? 'Monthly amounts the platform owes each academy for fees collected on their behalf.'
            : 'Monthly amounts Brightstars owes you for fees collected on your behalf, settled to your bank account.'
        }
      />

      <DataTable
        columns={columns}
        rows={settlements.data ?? []}
        rowKey={(s) => s.id}
        loading={settlements.isLoading}
      />
    </div>
  )
}
